/*
 * Agencies and clients (milestone 6): client orgs, invites, moving things
 * between an agency's orgs, and branding. Built against docs/milestone-6.md
 * until docs/api.md has "Agencies and clients (milestone 6)".
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { api } from "./client";
import { keys } from "./keys";
import type { ClientAccess, Role } from "./types";
import type { Brand } from "../lib/brand";
import { accessKey, type InviteAccess } from "./access";

export type ClientOrg = {
  id: string;
  name: string;
  slug: string;
  kind?: "client" | "agency";
  timezone?: string;
  createdAt: string;
  counts: { sites: number; servers: number; mailDomains: number; members: number; pendingInvites: number; /** Open ones past their date (an older API: absent). */ expiredInvites?: number };
};

export type Invite = {
  id: string;
  email: string;
  role: Role;
  orgId: string;
  status?: "pending" | "expired" | "accepted" | "revoked";
  invitedBy: { id: string; name: string } | null;
  createdAt: string;
  expiresAt: string;
  lastSentAt?: string | null;
  /** How its last email went ("sent", "pending", "failed", "skipped"; the list has it too), and why not when it didn't. */
  emailStatus?: string | null;
  emailError?: string | null;
  /** The invite link, only in the response that creates or resends it, so it can be sent another way; null for an address without an account (by email only). */
  url?: string | null;
  /** Team access carried by the invite, applied when it's accepted, and the API's sentence for it. */
  access?: InviteAccess | null;
  summary?: string | null;
  /** Groups they join once they accept. */
  groups?: { id: string; name: string }[];
  /** Their SSH key, added for the org when they accept. */
  sshKey?: { name: string; type: string; fingerprint: string } | null;
  /** A client invite: what they'll be able to do. */
  client?: ClientAccess;
  /** The address has an account, so a link can be copied for it (useInviteLink); absent from an older API. */
  hasAccount?: boolean;
};

/** What creating or resending an invite answers: the invite, with its link, and how the email went. */
export type InviteSent = { invite: Invite; email?: { status: string; error: string | null } | null };

export type MoveKind = "site" | "server" | "mail_domain";

export const agencyKeys = {
  clients: (orgId: string) => ["org", orgId, "clients"] as const,
  invites: (orgId: string) => ["org", orgId, "invites"] as const,
  branding: (orgId: string) => ["org", orgId, "branding"] as const,
};

export function useClients(enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: agencyKeys.clients(org.id),
    queryFn: ({ signal }) => api.get<{ clients: ClientOrg[] }>(`/orgs/${org.id}/clients`, signal),
    select: (d) => d.clients,
    enabled,
  });
}

export function useCreateClient() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    // planId: a new account's plan (platform admins; docs/plans.md); Standard when left out.
    mutationFn: (v: { name: string; kind?: "client" | "agency"; planId?: string | null }) => api.post<{ client: ClientOrg }>(`/orgs/${org.id}/clients`, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agencyKeys.clients(org.id) });
      // The new org shows in the org switcher.
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: ["platform", "accounts"] });
    },
  });
}

/** Invites for one org: the agency's own, or one of its clients'. */
export function useInvites(orgId: string, enabled = true) {
  return useQuery({
    queryKey: agencyKeys.invites(orgId),
    queryFn: ({ signal }) => api.get<{ invites: Invite[] }>(`/orgs/${orgId}/invites`, signal),
    select: (d) => d.invites,
    enabled,
  });
}

export function useInvite(orgId: string) {
  const qc = useQueryClient();
  const org = useOrg();
  return useMutation({
    mutationFn: (v: { email: string; role: Role; access?: InviteAccess; groups?: string[]; sshKey?: { publicKey: string; name?: string } }) => api.post<InviteSent>(`/orgs/${orgId}/invites`, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agencyKeys.invites(orgId) });
      // A site's "who can reach it" lists invites that carry access to it.
      void qc.invalidateQueries({ queryKey: accessKey(orgId) });
      void qc.invalidateQueries({ queryKey: agencyKeys.clients(org.id) });
    },
  });
}

export function useRevokeInvite(orgId: string) {
  const qc = useQueryClient();
  const org = useOrg();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/orgs/${orgId}/invites/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agencyKeys.invites(orgId) });
      void qc.invalidateQueries({ queryKey: accessKey(orgId) });
      void qc.invalidateQueries({ queryKey: agencyKeys.clients(org.id) });
    },
  });
}

export function useResendInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<InviteSent>(`/orgs/${orgId}/invites/${id}/resend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: agencyKeys.invites(orgId) }),
  });
}

/** A new link for an invite, beside the emailed one, to pass on another way: nothing is sent (POST /invites/:id/link). */
export function useInviteLink(orgId: string) {
  return useMutation({ mutationFn: (id: string) => api.post<{ url: string; expiresAt: string }>(`/orgs/${orgId}/invites/${id}/link`) });
}

/** Move a server to another org the agency owns (sites, domains and mail go through transfers: api/transfers.ts). */
export function useMoveResource() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kind: MoveKind; id: string; toOrgId: string }) =>
      api.post<{ ok: true; moved?: unknown }>(`/orgs/${org.id}/moves`, { type: v.kind, id: v.id, toOrgId: v.toOrgId }),
    onSuccess: (_r, v) => {
      // Both orgs' lists change.
      void qc.invalidateQueries({ queryKey: keys.org(org.id) });
      void qc.invalidateQueries({ queryKey: keys.org(v.toOrgId) });
    },
  });
}

/**
 * Hosterr gives a server to an account, or takes it back (platform admins; POST /moves from the platform org). Its
 * sites and mail keep their orgs.
 */
export function useGiveServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { platformId: string; serverId: string; fromOrgId: string; toOrgId: string }) => api.post<{ ok: true; moved: boolean }>(`/orgs/${v.platformId}/moves`, { type: "server", id: v.serverId, toOrgId: v.toOrgId }),
    onSuccess: (_r, v) => {
      for (const id of new Set([v.platformId, v.fromOrgId, v.toOrgId])) void qc.invalidateQueries({ queryKey: keys.org(id) });
    },
  });
}

/* ── Deleting an empty client ─────────────────────────────────────── */

/** What deleting a client would refuse over (named, as for an account), else who loses access. */
export type ClientDeletion = {
  client: { id: string; name: string };
  holds: { sites: string[]; servers: string[]; mail: string[]; domains: string[] };
  blocked: string | null;
  members: string[];
  invites: number;
};

export function useClientDeletion(clientId: string | null) {
  const org = useOrg();
  return useQuery({
    queryKey: ["org", org.id, "clients", clientId ?? "", "deletion"] as const,
    queryFn: ({ signal }) => api.get<ClientDeletion>(`/orgs/${org.id}/clients/${clientId}/deletion`, signal),
    enabled: !!clientId,
    retry: false,
    staleTime: 0,
  });
}

/** An agency's client with nothing left on it: gone, and its own people lose access (their accounts stay). */
export function useDeleteClient() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; confirmName: string }) => api.del<{ ok: true; removed: number }>(`/orgs/${org.id}/clients/${v.clientId}`, { confirmName: v.confirmName }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agencyKeys.clients(org.id) });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/* ── People ─────────────────────────────────────────────────────── */

/**
 * Remove someone from an org, or leave it yourself (`DELETE /orgs/:orgId/members/:userId`). Admins remove others; only
 * owners remove owners; an account keeps an owner (409 with the reason). Their site access goes with it.
 */
export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.del<{ ok: true }>(`/orgs/${orgId}/members/${userId}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.members(orgId) });
      void qc.invalidateQueries({ queryKey: accessKey(orgId) });
    },
  });
}

/* ── Restore requests (clients ask; the agency restores) ───────────── */

export type RestoreRequest = {
  id: string;
  orgId: string;
  orgName: string;
  orgSlug: string;
  siteId: string;
  siteName: string;
  environment: { id: string; name: string; slug: string | null; label: string } | null;
  backupId: string | null;
  note: string | null;
  status: "open" | "done" | "dismissed";
  /** What whoever handled it said; it's in the email to the person who asked. */
  reply: string | null;
  requestedBy: { id: string; name: string | null } | null;
  resolvedBy: { id: string; name: string | null } | null;
  createdAt: string;
  resolvedAt: string | null;
};

const restoreKey = (orgId: string) => ["org", orgId, "restore-requests"] as const;

/** Open requests across the agency's clients (and its own), for the people who can see the sites. */
export function useRestoreRequests(enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: [...restoreKey(org.id), "open"],
    queryFn: ({ signal }) => api.get<{ requests: RestoreRequest[] }>(`/orgs/${org.id}/restore-requests?includeClients=1&status=open`, signal),
    select: (d) => d.requests,
    enabled,
    retry: false,
  });
}

/** Done or dismissed (the person who asked is emailed, with the reply), or open again. */
export function useAnswerRestoreRequest() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: "open" | "done" | "dismissed"; reply?: string | null }) =>
      api.patch<{ request: RestoreRequest; told: string | null }>(`/orgs/${org.id}/restore-requests/${v.id}`, { status: v.status, reply: v.reply ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: restoreKey(org.id) });
      // The Overview's "needs a look" lists open requests.
      void qc.invalidateQueries({ queryKey: keys.org(org.id) });
    },
  });
}

/* ── A new account's setup checklist ──────────────────────────────── */

export type SetupStep = { key: "card" | "two_factor" | "team" | "branding" | "first_client"; done: boolean };

/** The Overview's checklist: shown for a new account's own owners and admins until it's done or dismissed. An older API: none. */
export function useSetup(enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: ["org", org.id, "setup"] as const,
    queryFn: ({ signal }) => api.get<{ show: boolean; steps: SetupStep[]; dismissedAt: string | null }>(`/orgs/${org.id}/setup`, signal),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useDismissSetup() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>(`/orgs/${org.id}/setup/dismiss`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org", org.id, "setup"] }),
  });
}

/* ── Branding ───────────────────────────────────────────────────── */

export type CustomDomainStatus = "pending" | "verified" | "failed";

export type CustomDomain = {
  domain: string;
  status: CustomDomainStatus;
  /** What's missing, in words. */
  detail: string | null;
  checkedAt: string | null;
  verifiedAt: string | null;
  /**
   * The CNAME to the platform's target, then the TXT challenge that proves the domain is the agency's, each with what
   * the last check saw (null: not checked yet): found, missing, pointing elsewhere, behind Cloudflare's proxy, or a
   * different value, and what it saw then.
   */
  records: { type: "CNAME" | "TXT"; name: string; value: string; status?: "found" | "missing" | "elsewhere" | "proxied" | "wrong" | null; seen?: string[] }[];
  /** Where the domain's DNS is hosted: the zone the records go in, and who runs it ("Cloudflare", "Hosterr"). Absent from an older API. */
  dnsHost?: { zone: string | null; provider: string | null; hosterrDomainId: string | null };
  /** Served now: verified while the agency shows its own brand. Absent from an older API. */
  served?: boolean;
};

/** What the Branding page edits: the settings (null means "use the default"), with the logos' addresses from the brand. */
export type OrgBranding = {
  /** "Your own brand" (true) or Hosterr's (false, the default). Absent from an older API: its own. */
  ownBrand?: boolean;
  /** Whether the agency chose (its own, or Hosterr's on purpose); until then the choice shows neither. Absent from an older API. */
  brandChosen?: boolean;
  displayName: string | null;
  /** Hex; null uses Hosterr's orange. */
  accent: string | null;
  emailFromName: string | null;
  supportEmail: string | null;
  supportUrl: string | null;
  /** An agency's own terms, shown to its clients beside Hosterr's on its brand. Absent from an older API. */
  termsUrl?: string | null;
  poweredBy: boolean;
  panelDomain: CustomDomain | null;
  webmailDomain: CustomDomain | null;
  logoLight: string | null;
  logoDark: string | null;
  favicon: string | null;
  /** Emails go out as "<sender name> <this address>", Hosterr's own sending address. */
  sendingAddress?: string | null;
  /** What the custom addresses' CNAME records point at (custom.hosterr.com). */
  target?: string;
  /** What it adds up to (with the defaults filled in): the agency's own brand as set. */
  brand: Brand;
};

type BrandingResponse = {
  settings: Omit<OrgBranding, "logoLight" | "logoDark" | "favicon" | "brand">;
  assets: Record<string, { contentType: string; width: number | null; height: number | null; updatedAt: string } | null>;
  brand: Brand;
};

export type BrandingPatch = Partial<Pick<OrgBranding, "ownBrand" | "displayName" | "accent" | "emailFromName" | "supportEmail" | "supportUrl" | "termsUrl" | "poweredBy">> & {
  /** A hostname to add (a fresh challenge, starting pending), or null to remove it. */
  panelDomain?: string | null;
  webmailDomain?: string | null;
};
export type BrandAsset = "logoLight" | "logoDark" | "favicon";

export function useBranding(enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: agencyKeys.branding(org.id),
    queryFn: ({ signal }) => api.get<BrandingResponse>(`/orgs/${org.id}/branding`, signal),
    select: (d): OrgBranding => ({ ...d.settings, logoLight: d.brand.logoLight, logoDark: d.brand.logoDark, favicon: d.brand.favicon, brand: d.brand }),
    enabled,
  });
}

/** Every branding change refetches the page's data and the brand the panel shows. */
function useBrandingMutation<V, R>(fn: (orgId: string, v: V) => Promise<R>) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(org.id, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agencyKeys.branding(org.id) });
      void qc.invalidateQueries({ queryKey: ["brand"] });
    },
  });
}

export const useUpdateBranding = () => useBrandingMutation<BrandingPatch, { settings: BrandingResponse["settings"]; brand: Brand }>((orgId, v) => api.patch(`/orgs/${orgId}/branding`, v));

/** An SVG (rebuilt from an allowlist by the API) or PNG, as a data URL. Null removes it. */
export const useUploadBrandAsset = () =>
  useBrandingMutation<{ kind: BrandAsset; dataUrl: string | null }, { brand: Brand }>((orgId, v) =>
    v.dataUrl ? api.put(`/orgs/${orgId}/branding/assets/${v.kind}`, { dataUrl: v.dataUrl }) : api.del(`/orgs/${orgId}/branding/assets/${v.kind}`),
  );

/** Back to the default look in one call (the custom addresses stay). The page shows the answer at once; the panel's brand refetches. */
export function useResetBranding() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<BrandingResponse>(`/orgs/${org.id}/branding/reset`),
    onSuccess: (d) => {
      qc.setQueryData(agencyKeys.branding(org.id), d);
      void qc.invalidateQueries({ queryKey: ["brand"] });
    },
  });
}

/** Check a custom domain now (otherwise every 15 minutes). */
export const useCheckCustomDomain = () =>
  useBrandingMutation<{ which: "panel" | "webmail" }, { settings: BrandingResponse["settings"] }>((orgId, v) => api.post(`/orgs/${orgId}/branding/domains/${v.which}/check`));
